import React, { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { TaskAlt } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { INSTANT_FORMATS, todayLocalISO } from '../utils/dateUtils';
import { useFormatInstant } from '../hooks/useClinicTimezone';
import { useAuth } from '../auth/useAuth';
import { reportFromError } from '../offline/connectivity';
import {
  deliveryAttestationService,
  DeliveryAttestationRecord,
} from '../services/deliveryAttestationService';

/**
 * VALSOFT proof-of-delivery attestation wall. Shown by DeliveryAttestationGuard
 * to OWNER/ADMIN users of a paid clinic until the one-time attestation is
 * submitted. The record is immutable — there is no edit path, by design.
 */
const DeliveryAttestationWall: React.FC = () => {
  const { user, logout } = useAuth();
  const queryClient = useQueryClient();
  const formatInstant = useFormatInstant();
  // LOCAL yyyy-mm-dd (not toISOString, which is UTC and drifts a day around midnight).
  const today = useMemo(() => todayLocalISO(), []);

  const [accessDate, setAccessDate] = useState('');
  const [ableToLogIn, setAbleToLogIn] = useState<'yes' | 'no' | null>(null);
  const [fullName, setFullName] = useState(user?.displayName || '');
  const [roleTitle, setRoleTitle] = useState('');
  const [attested, setAttested] = useState(false);
  const [error, setError] = useState('');
  const [record, setRecord] = useState<DeliveryAttestationRecord | null>(null);

  const submit = useMutation(deliveryAttestationService.submit, {
    onSuccess: (created) => setRecord(created),
    onError: (e: any) => {
      reportFromError(e);
      if (e?.response?.status === 409) {
        // Someone else at the clinic attested first — release the gate.
        queryClient.setQueryData(['delivery-attestation'], { required: false, attestation: null });
        return;
      }
      setError(
        e?.response?.data?.message ||
          'Could not submit right now. Check your connection and try again.',
      );
    },
  });

  const valid =
    Boolean(accessDate) &&
    ableToLogIn === 'yes' &&
    fullName.trim().length > 1 &&
    roleTitle.trim().length > 1 &&
    attested;

  const handleSubmit = () => {
    if (!valid || submit.isLoading) return;
    setError('');
    submit.mutate({
      accessReceivedDate: accessDate,
      ableToLogIn: true,
      fullName: fullName.trim(),
      role: roleTitle.trim(),
      attested: true,
    });
  };

  // The confirmed screen is dismissed by the user, not by a refetch — Continue
  // flips the cached status, which unmounts the wall (DeliveryAttestationGuard).
  const handleContinue = () => {
    queryClient.setQueryData(['delivery-attestation'], { required: false, attestation: record });
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ my: 6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box
          component="img"
          src="/StableTrack.png"
          alt="StableTrack"
          sx={{ height: 96, width: 96, mb: 3 }}
        />
        <Card sx={{ width: '100%', boxShadow: 3 }}>
          {record ? (
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <TaskAlt sx={{ fontSize: 60, color: 'success.main', mb: 1 }} />
              <Typography variant="h5" gutterBottom>
                Delivery confirmed
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Thanks — this has been recorded and can&apos;t be edited.
              </Typography>
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 3 }}>
                {[
                  ['Record ID', record.recordId],
                  ['Confirmed by', record.submittedByName],
                  ['Role', record.submittedByRole],
                  // parseISO on the date part only — a UTC-midnight DateTime rendered
                  // via new Date().toLocaleDateString() flips to the PREVIOUS day in
                  // all US timezones (see dateUtils.formatDob for the same trap).
                  ['Access date', format(parseISO(record.accessReceivedAt.substring(0, 10)), 'MMM d, yyyy')],
                  // DeliveryAttestation.createdAt is an INSTANT (`@default(now())`
                  // — DATE-KINDS.md 1.4) — the legal submission moment on an
                  // immutable record. The attestation PDF already prints it in the
                  // CLINIC's zone (delivery-attestation-pdf.ts:39-42); the web copy
                  // must not disagree with the document it is confirming.
                  [
                    'Submitted',
                    formatInstant(record.createdAt, INSTANT_FORMATS.dateTimeNumeric),
                  ],
                ].map(([k, v]) => (
                  <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                    <Typography variant="body2" color="text.secondary">
                      {k}
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {v}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Button fullWidth variant="contained" onClick={handleContinue}>
                Continue to StableTrack
              </Button>
            </CardContent>
          ) : (
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom>
                Confirm you&apos;ve received access to StableTrack
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                We keep a record of this for every practice we onboard. Once you submit, it&apos;s
                entered permanently and can&apos;t be changed — take a moment to check it over
                before you continue.
              </Typography>

              <Stack spacing={3}>
                <TextField
                  label="When did your practice receive access to StableTrack?"
                  type="date"
                  value={accessDate}
                  onChange={(e) => setAccessDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: today }}
                  fullWidth
                />

                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Are you able to log in and use StableTrack right now?
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    fullWidth
                    value={ableToLogIn}
                    onChange={(_e, v) => v && setAbleToLogIn(v)}
                  >
                    <ToggleButton value="yes" color="primary">
                      Yes, I can log in
                    </ToggleButton>
                    <ToggleButton value="no" color="error">
                      No, I&apos;m having trouble
                    </ToggleButton>
                  </ToggleButtonGroup>
                  {ableToLogIn === 'no' && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                      If you&apos;re not able to log in yet, submitting won&apos;t fix that —
                      contact StableTrack support first. You can confirm once it&apos;s sorted.
                    </Alert>
                  )}
                </Box>

                <TextField
                  label="Your name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  inputProps={{ maxLength: 200 }}
                  fullWidth
                />

                <TextField
                  label="Your role at this practice"
                  placeholder="e.g. Practice Owner, Office Manager"
                  value={roleTitle}
                  onChange={(e) => setRoleTitle(e.target.value)}
                  inputProps={{ maxLength: 200 }}
                  fullWidth
                />

                <Divider />

                <FormControlLabel
                  sx={{ alignItems: 'flex-start' }}
                  control={
                    <Checkbox
                      checked={attested}
                      onChange={(e) => setAttested(e.target.checked)}
                      sx={{ mt: -1 }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      <strong>I confirm</strong> that our practice has received access to
                      StableTrack, and that the information above is accurate. I understand this
                      record will be used for billing and audit purposes.
                    </Typography>
                  }
                />

                {error && <Alert severity="error">{error}</Alert>}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!valid || submit.isLoading}
                  >
                    {submit.isLoading ? 'Submitting…' : 'Confirm & continue'}
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    This can&apos;t be changed once submitted.
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="text"
                  onClick={logout}
                  sx={{ color: 'text.secondary', textTransform: 'none' }}
                >
                  Log out
                </Button>
              </Box>
            </CardContent>
          )}
        </Card>
      </Box>
    </Container>
  );
};

export default DeliveryAttestationWall;
